import { User } from '@nextforce/api'
import { Avatar, makeStyles, Text, useTheme } from '@rneui/themed'
import { t } from 'app/i18l'
import { font } from 'app/theme'
import React from 'react'
import { ActivityIndicator, Platform, View } from 'react-native'
import { TextInput } from 'react-native-gesture-handler'
import { SafeAreaView } from 'react-native-safe-area-context'
import { confirmationAlert } from 'shared/alerts'
import { pickImageFromLibrary } from 'shared/image_picker'

interface Props {
  user: User | undefined
  isPhotoUploading: boolean
  onAvatarChange: (avatar: string, mimeType: string) => void
  onAvatarRemove: () => void
  setFirstName: (fname: string) => void
  setLastName: (lname: string) => void
}

export function EditUsernameView({
  user,
  isPhotoUploading,
  onAvatarChange,
  onAvatarRemove,
  setFirstName,
  setLastName,
}: Props) {
  const { theme } = useTheme()
  const styles = useStyles()

  if (!user) {
    return null
  }

  return (
    <SafeAreaView edges={['left', 'right', 'bottom']} style={styles.root}>
      <View style={styles.container}>
        {isPhotoUploading ? (
          <View style={styles.imageActivityContainer}>
            <ActivityIndicator />
          </View>
        ) : (
          <Avatar
            key={user.photoUrl}
            rounded
            size="large"
            title={!!user.photoUrl ? undefined : user.initials}
            source={
              user.photoUrl
                ? {
                    uri: user.photoUrl,
                  }
                : undefined
            }
            titleStyle={styles.avatarTitle}
            containerStyle={styles.avatarContainer}
          >
            {Platform.OS !== 'web' && (
              <Avatar.Accessory
                onPress={async () => {
                  const pickImageResult = await pickImageFromLibrary()

                  if (pickImageResult.status === 'success') {
                    onAvatarChange(
                      pickImageResult.files[0].uri,
                      pickImageResult.files[0].mimeType
                    )
                  }
                }}
                size={20}
              />
            )}
            {user.hasProfilePhoto && (
              <Avatar.Accessory
                type="font-awesome-5"
                name="times"
                size={20}
                solid
                style={{ left: 0 }}
                onPress={async () => {
                  const isConfirmed = await confirmationAlert(
                    t('removePhotoTitle'),
                    t('removePhotoText')
                  )
                  if (isConfirmed) {
                    onAvatarRemove()
                  }
                }}
              />
            )}
          </Avatar>
        )}
        {Platform.OS !== 'web' && (
          <Text
            style={{
              paddingVertical: 10,
              textAlign: 'center',
              textAlignVertical: 'center',
            }}
          >
            {t('avatarDesc')}
          </Text>
        )}
        <View style={{ width: '100%' }}>
          <TextInput
            style={styles.profileUserName}
            defaultValue={user.firstName}
            selectTextOnFocus
            onChangeText={setFirstName}
            selectionColor={theme.colors?.primary}
          ></TextInput>
          <TextInput
            style={styles.profileUserName}
            defaultValue={user.lastName}
            selectTextOnFocus
            onChangeText={setLastName}
            selectionColor={theme.colors?.primary}
          ></TextInput>
        </View>
      </View>
    </SafeAreaView>
  )
}

const useStyles = makeStyles(theme => ({
  root: {
    flex: 1,
  },
  container: {
    padding: 20,
    flex: 1,
    alignItems: 'center',
  },
  avatarContainer: {
    backgroundColor: '#FFE2B3',
    marginBottom: 10,
  },
  avatarTitle: {
    color: theme.colors.red,
    fontSize: 30,
    fontFamily: font.bold,
  },
  profileUserName: {
    color: theme.colors?.grey0,
    fontSize: 16,
    marginTop: 10,
    padding: 5,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors?.grey3,
  },
  imageActivityContainer: {
    width: 75,
    height: 75,
    borderRadius: 100,
    justifyContent: 'center',
    backgroundColor: '#FFE2B3',
  },
}))
